import { PRICING_MODEL } from '@monorepo/shared/schemas/common'
import { useQuery } from '@tanstack/react-query'
import { DollarSign } from 'lucide-react'
import type { Plan } from '@/features/plans/types/plan.types'
import { Badge } from '@/shared/components/ui/badge'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { Separator } from '@/shared/components/ui/separator'
import type {
  AddonPlan,
  ContractTaxResponse,
} from '../../services/contractService'
import { contractService } from '../../services/contractService'
import type { Customer } from '../../services/customerService'
import type { EditableCharge } from '../../types/customerDetail.types'

/** Addon selection entry passed in from the parent form */
interface AddonEntry {
  plan_id: string
  name?: string
  quantity?: number
}

/** Discount entry passed in from the parent form */
interface DiscountEntry {
  name: string
  type: 'percentage' | 'fixed_amount'
  percentage_off?: number
  amount_off?: number
  discount?: number
  amount?: number
}

/** Computed discount detail with calculated amount */
interface DiscountDetail extends DiscountEntry {
  calculatedAmount: number
}

/** Computed tax detail with calculated amount */
interface TaxDetail extends ContractTaxResponse {
  calculatedAmount: number
}

/** Breakdown item for addon charges */
interface AddonChargeItem {
  name: string
  amount: number
  type: string
  minCommitment?: number
}

/** Addon tier (from addon plan features) */
interface AddonFeatureTier {
  firstUnit?: number
  lastUnit?: number | null
  perUnitPrice?: number
}

/** Addon plan feature */
interface AddonPlanFeature {
  feature_name?: string
  signal_name?: string
  minimum_commitment_units?: number
  pricing_model?: string
  tier_pricing?: AddonFeatureTier[]
  unit_price?: number
}

/** Addon plan agent signal */
interface AddonAgentSignal {
  signal_name: string
  minimum_commitment_units?: number
  pricing_model?: string
  tiers?: Array<{
    minUnits?: number
    maxUnits?: number | null
    unitPrice?: number | string
  }>
  unit_price?: number
}

/** Addon plan agent */
interface AddonPlanAgent {
  signals?: AddonAgentSignal[]
}

/** Addon plan charge */
interface AddonPlanCharge {
  name: string
  type: string
  amount: string | number
}

interface ContractPreviewCardProps {
  customer: Customer | null
  selectedPlan: Plan | null
  startDate: Date | undefined
  endDate: Date | undefined
  isForever: boolean
  currency: string
  editableCharges: EditableCharge[]
  addons: AddonEntry[]
  discounts: DiscountEntry[]
  taxes: ContractTaxResponse[]
}

export function ContractPreviewCard({
  customer,
  selectedPlan,
  startDate,
  endDate,
  isForever,
  currency,
  editableCharges,
  addons,
  discounts,
  taxes,
}: ContractPreviewCardProps) {
  // Fetch addon plan details
  const { data: availableAddonPlans = [] } = useQuery({
    queryKey: ['addon-plans'],
    queryFn: contractService.getAvailableAddons,
    enabled: addons.length > 0,
  })
  const calculateAddonBreakdown = (addonPlan: AddonPlan | undefined) => {
    if (!addonPlan)
      return {
        oneTime: [] as AddonChargeItem[],
        recurring: [] as AddonChargeItem[],
        usage: [] as AddonChargeItem[],
      }

    const oneTime: AddonChargeItem[] = []
    const recurring: AddonChargeItem[] = []
    const usage: AddonChargeItem[] = []

    // Process charges
    if (addonPlan.charges) {
      ;(addonPlan.charges as unknown as AddonPlanCharge[]).forEach(
        (charge: AddonPlanCharge) => {
          if (charge.type === 'one_time') {
            oneTime.push({
              name: charge.name,
              amount: parseFloat(String(charge.amount)) || 0,
              type: 'fixed',
            })
          } else if (charge.type === 'recurring') {
            recurring.push({
              name: charge.name,
              amount: parseFloat(String(charge.amount)) || 0,
              type: 'recurring',
            })
          }
        }
      )
    }

    // Process features
    if (addonPlan.features) {
      ;(addonPlan.features as unknown as AddonPlanFeature[]).forEach(
        (feature: AddonPlanFeature) => {
          const minCommitment = feature.minimum_commitment_units || 0
          if (minCommitment > 0) {
            let cost = 0

            if (feature.pricing_model === 'tiered' && feature.tier_pricing) {
              let remainingUnits = minCommitment
              for (const tier of feature.tier_pricing) {
                if (remainingUnits <= 0) break
                const tierMax =
                  tier.lastUnit === null || tier.lastUnit === undefined
                    ? Infinity
                    : tier.lastUnit
                const tierMin = tier.firstUnit || 1
                const tierRange =
                  tierMax === Infinity ? remainingUnits : tierMax - tierMin + 1
                const unitsInThisTier = Math.min(remainingUnits, tierRange)
                const tierPrice = tier.perUnitPrice || 0
                cost += unitsInThisTier * tierPrice
                remainingUnits -= unitsInThisTier
              }
            } else if (
              feature.pricing_model === 'volume' &&
              feature.tier_pricing
            ) {
              const applicableTier = feature.tier_pricing.find(
                (tier: AddonFeatureTier) =>
                  minCommitment >= (tier.firstUnit || 1) &&
                  (tier.lastUnit === null ||
                    tier.lastUnit === undefined ||
                    minCommitment <= tier.lastUnit)
              )
              if (applicableTier) {
                cost = minCommitment * (applicableTier.perUnitPrice || 0)
              }
            } else {
              cost = minCommitment * (feature.unit_price || 0)
            }

            usage.push({
              name: feature.feature_name || feature.signal_name || '',
              amount: cost,
              type: 'feature',
              minCommitment,
            })
          }
        }
      )
    }

    // Process agents
    if (addonPlan.agents) {
      ;(addonPlan.agents as unknown as AddonPlanAgent[]).forEach(
        (agent: AddonPlanAgent) => {
          if (agent.signals) {
            agent.signals.forEach((signal: AddonAgentSignal) => {
              const minCommitment = signal.minimum_commitment_units || 0
              if (minCommitment > 0) {
                let cost = 0

                if (
                  signal.pricing_model === 'tiered' &&
                  signal.tiers &&
                  signal.tiers.length > 0
                ) {
                  let remainingUnits = minCommitment
                  for (const tier of signal.tiers) {
                    if (remainingUnits <= 0) break
                    const tierMax =
                      tier.maxUnits === null || tier.maxUnits === undefined
                        ? Infinity
                        : tier.maxUnits
                    const tierMin = tier.minUnits || 1
                    const tierRange =
                      tierMax === Infinity
                        ? remainingUnits
                        : tierMax - tierMin + 1
                    const unitsInThisTier = Math.min(remainingUnits, tierRange)
                    const tierPrice = parseFloat(String(tier.unitPrice)) || 0
                    cost += unitsInThisTier * tierPrice
                    remainingUnits -= unitsInThisTier
                  }
                } else if (
                  signal.pricing_model === 'volume' &&
                  signal.tiers &&
                  signal.tiers.length > 0
                ) {
                  const applicableTier = signal.tiers.find(
                    tier =>
                      minCommitment >= (tier.minUnits || 1) &&
                      (tier.maxUnits === null ||
                        tier.maxUnits === undefined ||
                        minCommitment <= tier.maxUnits)
                  )
                  if (applicableTier) {
                    cost =
                      minCommitment *
                      (parseFloat(String(applicableTier.unitPrice)) || 0)
                  }
                } else {
                  cost = minCommitment * (signal.unit_price || 0)
                }

                usage.push({
                  name: signal.signal_name,
                  amount: cost,
                  type: 'agent',
                  minCommitment,
                })
              }
            })
          }
        }
      )
    }

    return { oneTime, recurring, usage }
  }

  const calculateChargeBreakdown = () => {
    const oneTimeCharges = editableCharges.filter(c => c.category === 'fixed')
    const recurringCharges = editableCharges.filter(
      c => c.category === 'recurring'
    )
    const usageCharges = editableCharges.filter(
      c => c.category === 'agent' || c.category === 'feature'
    )

    const oneTimeTotal = oneTimeCharges.reduce((total, charge) => {
      return total + (charge.unitPrice || 0)
    }, 0)

    const recurringTotal = recurringCharges.reduce((total, charge) => {
      return total + (charge.unitPrice || 0)
    }, 0)

    // For usage charges, only count if there's a minimum commitment
    const usageTotal = usageCharges.reduce((total, charge) => {
      const minCommitment =
        charge.minCommitment || charge.minimum_commitment || 0
      if (minCommitment && minCommitment > 0) {
        if (
          charge.pricingModel === PRICING_MODEL.package &&
          charge.packageSize &&
          charge.packagePrice
        ) {
          // Package pricing: calculate packages needed for minimum commitment
          const packagesNeeded = Math.ceil(minCommitment / charge.packageSize)
          return total + packagesNeeded * charge.packagePrice
        } else if (
          (charge.pricingModel === PRICING_MODEL.tiered ||
            charge.pricingModel === PRICING_MODEL.volume) &&
          charge.tiers &&
          charge.tiers.length > 0
        ) {
          // Calculate cost through tiers based on minimum commitment
          let cost = 0
          let remainingUnits = minCommitment

          for (const tier of charge.tiers) {
            if (remainingUnits <= 0) break

            const tierMax =
              tier.max_units || tier.maxUnits || tier.lastUnit || Infinity
            const tierMin =
              tier.min_units || tier.minUnits || tier.firstUnit || 1
            const tierRange =
              tierMax === Infinity ? remainingUnits : tierMax - tierMin + 1
            const unitsInThisTier = Math.min(remainingUnits, tierRange)

            const tierPrice =
              tier.perUnitPrice ||
              parseFloat(String(tier.unit_price)) ||
              parseFloat(String(tier.unitPrice)) ||
              0
            cost += unitsInThisTier * tierPrice
            remainingUnits -= unitsInThisTier
          }

          return total + cost
        } else {
          // Flat pricing
          return total + minCommitment * (charge.unitPrice || 0)
        }
      }
      return total
    }, 0)

    return {
      oneTimeCharges,
      recurringCharges,
      usageCharges,
      oneTimeTotal,
      recurringTotal,
      usageTotal,
      grandTotal: oneTimeTotal + recurringTotal + usageTotal,
    }
  }

  const formatDate = (date: Date | undefined) => {
    if (!date) return ''
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (!selectedPlan && addons.length === 0) {
    return (
      <Card className='bg-transparent border'>
        <CardHeader>
          <CardTitle className='text-lg'>Contract Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='text-center py-8 text-muted-foreground'>
            <DollarSign className='w-12 h-12 mx-auto mb-4 opacity-50' />
            <p>Select a plan or add addons to preview your contract</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className='bg-transparent border-2'>
      <CardHeader className='pb-4'>
        <CardTitle className='text-lg'>Contract Preview</CardTitle>
        <div className='text-sm text-muted-foreground'>
          {customer?.name || 'Customer'}
        </div>
      </CardHeader>
      <CardContent className='space-y-6'>
        {/* Plan Info */}
        {selectedPlan ? (
          <div>
            <div className='flex items-center justify-between mb-2'>
              <span className='font-medium'>{selectedPlan.name}</span>
              <Badge variant='secondary'>{selectedPlan.billing_period}</Badge>
            </div>
            <div className='text-sm text-muted-foreground'>
              {selectedPlan.description || 'No description available'}
            </div>
          </div>
        ) : (
          <div>
            <div className='flex items-center justify-between mb-2'>
              <span className='font-medium'>Add-ons Only Contract</span>
              <Badge variant='outline'>custom</Badge>
            </div>
            <div className='text-sm text-muted-foreground'>
              Contract with add-ons only (no base plan selected)
            </div>
          </div>
        )}

        <Separator />

        {/* Contract Details */}
        <div className='space-y-3'>
          <div className='flex justify-between'>
            <span className='text-sm text-muted-foreground'>Start Date</span>
            <span className='text-sm font-medium'>{formatDate(startDate)}</span>
          </div>
          <div className='flex justify-between'>
            <span className='text-sm text-muted-foreground'>End Date</span>
            <span className='text-sm font-medium'>
              {isForever ? 'Forever' : formatDate(endDate)}
            </span>
          </div>
          <div className='flex justify-between'>
            <span className='text-sm text-muted-foreground'>Currency</span>
            <span className='text-sm font-medium'>{currency}</span>
          </div>
        </div>

        <Separator />

        {/* Charges Breakdown */}
        {(() => {
          const breakdown = calculateChargeBreakdown()

          return (
            <div className='space-y-4'>
              <h4 className='font-medium text-sm'>
                {selectedPlan ? 'Plan Charges' : 'Base Charges'}
              </h4>

              {/* Show message when no plan charges but addons exist */}
              {!selectedPlan &&
                breakdown.oneTimeCharges.length === 0 &&
                breakdown.recurringCharges.length === 0 &&
                breakdown.usageCharges.length === 0 &&
                addons.length > 0 && (
                  <div className='text-sm text-muted-foreground italic'>
                    No base plan charges. See add-ons below for contract
                    details.
                  </div>
                )}

              {/* One-time costs */}
              {breakdown.oneTimeCharges.length > 0 && (
                <div className='space-y-2'>
                  <div className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                    One-time costs
                  </div>
                  {breakdown.oneTimeCharges.map(charge => (
                    <div
                      key={charge.id}
                      className='flex justify-between items-center'
                    >
                      <div>
                        <div className='text-sm'>{charge.name}</div>
                        <div className='text-xs text-muted-foreground'>
                          fixed
                        </div>
                      </div>
                      <div className='text-right'>
                        <div className='text-sm font-medium'>
                          ${charge.unitPrice.toFixed(2)}
                        </div>
                        {charge.isEdited && (
                          <div className='text-xs text-orange-600'>
                            Modified
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Recurring costs */}
              {breakdown.recurringCharges.length > 0 && (
                <div className='space-y-2'>
                  <div className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                    Recurring costs ({selectedPlan?.billing_period || 'monthly'}
                    )
                  </div>
                  {breakdown.recurringCharges.map(charge => (
                    <div
                      key={charge.id}
                      className='flex justify-between items-center'
                    >
                      <div>
                        <div className='text-sm'>{charge.name}</div>
                        <div className='text-xs text-muted-foreground'>
                          recurring
                        </div>
                      </div>
                      <div className='text-right'>
                        <div className='text-sm font-medium'>
                          ${charge.unitPrice.toFixed(2)}
                        </div>
                        {charge.isEdited && (
                          <div className='text-xs text-orange-600'>
                            Modified
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Usage-based costs */}
              {breakdown.usageCharges.length > 0 && (
                <div className='space-y-2'>
                  <div className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                    Usage-based costs (minimum commitments)
                  </div>
                  {breakdown.usageCharges.map(charge => {
                    const minCommitment =
                      charge.minCommitment || charge.minimum_commitment || 0
                    const hasMinCommitment = minCommitment && minCommitment > 0
                    let cost = 0

                    if (hasMinCommitment) {
                      if (
                        charge.pricingModel === PRICING_MODEL.package &&
                        charge.packageSize &&
                        charge.packagePrice
                      ) {
                        // Package pricing: calculate packages needed
                        const packagesNeeded = Math.ceil(
                          minCommitment / charge.packageSize
                        )
                        cost = packagesNeeded * charge.packagePrice
                      } else if (
                        (charge.pricingModel === PRICING_MODEL.tiered ||
                          charge.pricingModel === PRICING_MODEL.volume) &&
                        charge.tiers &&
                        charge.tiers.length > 0
                      ) {
                        // Calculate tiered cost
                        let remainingUnits = minCommitment
                        for (const tier of charge.tiers) {
                          if (remainingUnits <= 0) break
                          const tierMax =
                            tier.max_units ||
                            tier.maxUnits ||
                            tier.lastUnit ||
                            Infinity
                          const tierMin =
                            tier.min_units ||
                            tier.minUnits ||
                            tier.firstUnit ||
                            1
                          const tierRange =
                            tierMax === Infinity
                              ? remainingUnits
                              : tierMax - tierMin + 1
                          const unitsInThisTier = Math.min(
                            remainingUnits,
                            tierRange
                          )
                          const tierPrice =
                            tier.perUnitPrice ||
                            parseFloat(String(tier.unit_price)) ||
                            parseFloat(String(tier.unitPrice)) ||
                            0
                          cost += unitsInThisTier * tierPrice
                          remainingUnits -= unitsInThisTier
                        }
                      } else {
                        // Flat pricing
                        cost = minCommitment * (charge.unitPrice || 0)
                      }
                    }

                    return (
                      <div
                        key={charge.id}
                        className='flex justify-between items-center'
                      >
                        <div>
                          <div className='text-sm'>{charge.name}</div>
                          <div className='text-xs text-muted-foreground'>
                            {charge.category}
                          </div>
                        </div>
                        <div className='text-right'>
                          <div className='text-sm font-medium'>
                            {hasMinCommitment ? `$${cost.toFixed(2)}` : '$0.00'}
                          </div>
                          <div className='text-xs text-muted-foreground'>
                            {hasMinCommitment
                              ? `${minCommitment} units min`
                              : 'pay as you go'}
                          </div>
                          {charge.isEdited && (
                            <div className='text-xs text-orange-600'>
                              Modified
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })()}

        {/* Addons */}
        {addons.length > 0 && (
          <>
            <Separator />
            <div className='space-y-4'>
              <h4 className='font-medium text-sm'>Add-ons</h4>
              {addons.map((addon, index) => {
                const addonPlan = availableAddonPlans.find(
                  (plan: AddonPlan) => plan.id === addon.plan_id
                )
                const addonBreakdown = calculateAddonBreakdown(addonPlan)

                return (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                    key={index}
                    className='space-y-3 p-3 border border-muted-foreground/20 rounded-lg'
                  >
                    <div className='flex justify-between items-center'>
                      <span className='font-medium text-sm'>
                        {addon.name || addonPlan?.name || 'Selected Addon'}
                      </span>
                      <Badge variant='outline' className='text-xs'>
                        addon
                      </Badge>
                    </div>

                    {/* One-time costs for addon */}
                    {addonBreakdown.oneTime.length > 0 && (
                      <div className='space-y-2'>
                        <div className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                          One-time costs
                        </div>
                        {addonBreakdown.oneTime.map((charge, chargeIndex) => (
                          <div
                            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                            key={chargeIndex}
                            className='flex justify-between items-center'
                          >
                            <div>
                              <div className='text-sm'>{charge.name}</div>
                              <div className='text-xs text-muted-foreground'>
                                {charge.type}
                              </div>
                            </div>
                            <div className='text-sm font-medium'>
                              ${charge.amount.toFixed(2)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Recurring costs for addon */}
                    {addonBreakdown.recurring.length > 0 && (
                      <div className='space-y-2'>
                        <div className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                          Recurring costs (monthly)
                        </div>
                        {addonBreakdown.recurring.map((charge, chargeIndex) => (
                          <div
                            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                            key={chargeIndex}
                            className='flex justify-between items-center'
                          >
                            <div>
                              <div className='text-sm'>{charge.name}</div>
                              <div className='text-xs text-muted-foreground'>
                                {charge.type}
                              </div>
                            </div>
                            <div className='text-sm font-medium'>
                              ${charge.amount.toFixed(2)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Usage-based costs for addon */}
                    {addonBreakdown.usage.length > 0 && (
                      <div className='space-y-2'>
                        <div className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                          Usage-based costs (minimum commitments)
                        </div>
                        {addonBreakdown.usage.map((charge, chargeIndex) => (
                          <div
                            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                            key={chargeIndex}
                            className='flex justify-between items-center'
                          >
                            <div>
                              <div className='text-sm'>{charge.name}</div>
                              <div className='text-xs text-muted-foreground'>
                                {charge.type}
                              </div>
                            </div>
                            <div className='text-right'>
                              <div className='text-sm font-medium'>
                                ${charge.amount.toFixed(2)}
                              </div>
                              <div className='text-xs text-muted-foreground'>
                                {charge.minCommitment} units min
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}

        {/* Discounts */}
        {discounts.length > 0 &&
          (() => {
            const breakdown = calculateChargeBreakdown()

            // Calculate addon totals for discount calculation
            let addonRecurringTotal = 0
            let addonUsageTotal = 0

            addons.forEach(addon => {
              const addonPlan = availableAddonPlans.find(
                (plan: AddonPlan) => plan.id === addon.plan_id
              )
              if (addonPlan) {
                const addonBreakdown = calculateAddonBreakdown(addonPlan)
                addonRecurringTotal += addonBreakdown.recurring.reduce(
                  (sum, charge) => sum + charge.amount,
                  0
                )
                addonUsageTotal += addonBreakdown.usage.reduce(
                  (sum, charge) => sum + charge.amount,
                  0
                )
              }
            })

            // Calculate subtotal before discounts (recurring + usage for monthly)
            const subtotalBeforeDiscount =
              breakdown.recurringTotal +
              breakdown.usageTotal +
              addonRecurringTotal +
              addonUsageTotal

            // Calculate total discount amount
            let totalDiscountAmount = 0
            const discountDetails: DiscountDetail[] = discounts.map(
              discount => {
                let discountValue = 0

                // Handle percentage discount
                if (discount.type === 'percentage') {
                  const percentageOff =
                    discount.percentage_off ||
                    (discount.discount ? discount.discount / 100 : 0)
                  discountValue = subtotalBeforeDiscount * percentageOff
                }
                // Handle fixed amount discount
                else if (discount.type === 'fixed_amount') {
                  discountValue =
                    discount.amount_off ||
                    discount.discount ||
                    discount.amount ||
                    0
                }

                totalDiscountAmount += discountValue

                return {
                  ...discount,
                  calculatedAmount: discountValue,
                }
              }
            )

            const subtotalAfterDiscount = Math.max(
              0,
              subtotalBeforeDiscount - totalDiscountAmount
            )

            return (
              <>
                <Separator />
                <div className='space-y-3'>
                  <h4 className='font-medium text-sm'>Discounts</h4>

                  {/* Subtotal before discount */}
                  <div className='flex justify-between text-sm'>
                    <span className='text-muted-foreground'>
                      Subtotal (before discounts):
                    </span>
                    <span className='font-medium'>
                      ${subtotalBeforeDiscount.toFixed(2)}
                    </span>
                  </div>

                  {/* List each discount */}
                  {discountDetails.map(
                    (discount: DiscountDetail, index: number) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      <div key={index} className='flex justify-between'>
                        <span className='text-sm text-green-600'>
                          {discount.name}
                          {discount.type === 'percentage' && (
                            <span className='text-xs ml-1'>
                              (
                              {(
                                (discount.percentage_off ||
                                  (discount.discount || 0) / 100) * 100
                              ).toFixed(0)}
                              %)
                            </span>
                          )}
                        </span>
                        <span className='text-sm font-medium text-green-600'>
                          -${discount.calculatedAmount.toFixed(2)}
                        </span>
                      </div>
                    )
                  )}

                  {/* Subtotal after discount */}
                  <div className='flex justify-between text-sm border-t pt-2'>
                    <span className='font-medium'>After discount:</span>
                    <span className='font-bold'>
                      ${subtotalAfterDiscount.toFixed(2)}
                    </span>
                  </div>
                </div>
              </>
            )
          })()}

        <Separator />

        {/* Taxes */}
        {taxes.length > 0 &&
          (() => {
            const breakdown = calculateChargeBreakdown()

            // Calculate addon totals
            let addonRecurringTotal = 0
            let addonUsageTotal = 0

            addons.forEach(addon => {
              const addonPlan = availableAddonPlans.find(
                (plan: AddonPlan) => plan.id === addon.plan_id
              )
              if (addonPlan) {
                const addonBreakdown = calculateAddonBreakdown(addonPlan)
                addonRecurringTotal += addonBreakdown.recurring.reduce(
                  (sum, charge) => sum + charge.amount,
                  0
                )
                addonUsageTotal += addonBreakdown.usage.reduce(
                  (sum, charge) => sum + charge.amount,
                  0
                )
              }
            })

            // Calculate subtotal (charges + addons)
            const subtotalBeforeDiscount =
              breakdown.recurringTotal +
              breakdown.usageTotal +
              addonRecurringTotal +
              addonUsageTotal

            // Calculate total discount
            let totalDiscountAmount = 0
            discounts.forEach(discount => {
              if (discount.type === 'percentage') {
                const percentageOff =
                  discount.percentage_off ||
                  (discount.discount ? discount.discount / 100 : 0)
                totalDiscountAmount += subtotalBeforeDiscount * percentageOff
              } else if (discount.type === 'fixed_amount') {
                totalDiscountAmount +=
                  discount.amount_off ||
                  discount.discount ||
                  discount.amount ||
                  0
              }
            })

            // Taxable amount = subtotal - discounts
            const taxableAmount = Math.max(
              0,
              subtotalBeforeDiscount - totalDiscountAmount
            )

            // Calculate tax amounts
            const taxDetails: TaxDetail[] = taxes.map(tax => {
              let taxAmount = 0
              const taxType = tax.tax_type

              if (taxType === 'percentage') {
                taxAmount = taxableAmount * (tax.rate || 0)
              } else if (taxType === 'fixed' && tax.fixed_amount_cents) {
                taxAmount = tax.fixed_amount_cents / 100
              }

              return {
                ...tax,
                calculatedAmount: taxAmount,
              }
            })

            const totalTaxAmount = taxDetails.reduce(
              (sum: number, tax: TaxDetail) => sum + tax.calculatedAmount,
              0
            )

            return (
              <>
                <div className='space-y-3'>
                  <h4 className='font-medium text-sm'>Taxes</h4>

                  {/* Taxable amount */}
                  <div className='flex justify-between text-sm'>
                    <span className='text-muted-foreground'>
                      Taxable amount:
                    </span>
                    <span className='font-medium'>
                      ${taxableAmount.toFixed(2)}
                    </span>
                  </div>

                  {/* List each tax */}
                  {taxDetails.map((tax: TaxDetail, index: number) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                    <div key={index} className='flex justify-between'>
                      <span className='text-sm text-amber-600'>
                        {tax.name}
                        {tax.tax_type === 'percentage' && (
                          <span className='text-xs ml-1'>
                            ({((tax.rate || 0) * 100).toFixed(2)}%)
                          </span>
                        )}
                      </span>
                      <span className='text-sm font-medium text-amber-600'>
                        +${tax.calculatedAmount.toFixed(2)}
                      </span>
                    </div>
                  ))}

                  {/* Total taxes */}
                  <div className='flex justify-between text-sm border-t pt-2'>
                    <span className='font-medium'>Total taxes:</span>
                    <span className='font-bold text-amber-600'>
                      +${totalTaxAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
                <Separator />
              </>
            )
          })()}

        {/* Total */}
        {(() => {
          const breakdown = calculateChargeBreakdown()

          // Calculate addon totals
          let addonOneTimeTotal = 0
          let addonRecurringTotal = 0
          let addonUsageTotal = 0

          addons.forEach(addon => {
            const addonPlan = availableAddonPlans.find(
              (plan: AddonPlan) => plan.id === addon.plan_id
            )
            if (addonPlan) {
              const addonBreakdown = calculateAddonBreakdown(addonPlan)
              addonOneTimeTotal += addonBreakdown.oneTime.reduce(
                (sum, charge) => sum + charge.amount,
                0
              )
              addonRecurringTotal += addonBreakdown.recurring.reduce(
                (sum, charge) => sum + charge.amount,
                0
              )
              addonUsageTotal += addonBreakdown.usage.reduce(
                (sum, charge) => sum + charge.amount,
                0
              )
            }
          })

          // Combined totals (plan + addons)
          const totalOneTime = breakdown.oneTimeTotal + addonOneTimeTotal
          const totalRecurring = breakdown.recurringTotal + addonRecurringTotal
          const totalUsage = breakdown.usageTotal + addonUsageTotal

          // Calculate subtotal before discounts (monthly charges)
          const monthlySubtotal = totalRecurring + totalUsage

          // Calculate total discount
          let totalDiscountAmount = 0
          discounts.forEach(discount => {
            if (discount.type === 'percentage') {
              const percentageOff =
                discount.percentage_off ||
                (discount.discount ? discount.discount / 100 : 0)
              totalDiscountAmount += monthlySubtotal * percentageOff
            } else if (discount.type === 'fixed_amount') {
              totalDiscountAmount +=
                discount.amount_off || discount.discount || discount.amount || 0
            }
          })

          // After discount amount
          const afterDiscount = Math.max(
            0,
            monthlySubtotal - totalDiscountAmount
          )

          // Calculate tax amounts
          let totalTaxAmount = 0
          taxes.forEach(tax => {
            const taxType = tax.tax_type
            if (taxType === 'percentage') {
              totalTaxAmount += afterDiscount * (tax.rate || 0)
            } else if (taxType === 'fixed' && tax.fixed_amount_cents) {
              totalTaxAmount += tax.fixed_amount_cents / 100
            }
          })

          // Final totals
          // Monthly total = (charges + addons - discounts) + taxes
          const monthlyTotalWithTax = afterDiscount + totalTaxAmount

          // First month total = one-time + monthly total with tax
          const firstMonthTotal = totalOneTime + monthlyTotalWithTax

          return (
            <div className='space-y-3'>
              <div className='space-y-2 text-sm'>
                {totalOneTime > 0 && (
                  <div className='flex justify-between'>
                    <span className='text-muted-foreground'>
                      One-time charges:
                    </span>
                    <span className='font-medium'>
                      ${totalOneTime.toFixed(2)}
                    </span>
                  </div>
                )}
                {monthlySubtotal > 0 && (
                  <div className='flex justify-between'>
                    <span className='text-muted-foreground'>
                      Monthly charges:
                    </span>
                    <span className='font-medium'>
                      ${monthlySubtotal.toFixed(2)}
                    </span>
                  </div>
                )}
                {totalDiscountAmount > 0 && (
                  <div className='flex justify-between'>
                    <span className='text-muted-foreground text-green-600'>
                      Discounts:
                    </span>
                    <span className='font-medium text-green-600'>
                      -${totalDiscountAmount.toFixed(2)}
                    </span>
                  </div>
                )}
                {totalTaxAmount > 0 && (
                  <div className='flex justify-between'>
                    <span className='text-muted-foreground text-amber-600'>
                      Taxes:
                    </span>
                    <span className='font-medium text-amber-600'>
                      +${totalTaxAmount.toFixed(2)}
                    </span>
                  </div>
                )}
                {addons.length > 0 &&
                  (addonOneTimeTotal > 0 ||
                    addonRecurringTotal > 0 ||
                    addonUsageTotal > 0) && (
                    <div className='text-xs text-muted-foreground italic'>
                      (includes {addons.length} addon
                      {addons.length > 1 ? 's' : ''})
                    </div>
                  )}
              </div>

              {/* Monthly total with tax (if no one-time charges) */}
              {totalOneTime === 0 && monthlyTotalWithTax > 0 && (
                <div className='flex justify-between items-center p-3 border border-muted-foreground/30 rounded-lg'>
                  <span className='font-medium'>Monthly total</span>
                  <span className='font-bold text-lg'>
                    ${monthlyTotalWithTax.toFixed(2)}
                  </span>
                </div>
              )}

              {/* First month total (if has one-time charges) */}
              {totalOneTime > 0 && (
                <div className='flex justify-between items-center p-3 border-2 border-primary/30 rounded-lg bg-primary/5'>
                  <div>
                    <span className='font-medium'>First month total</span>
                    <div className='text-xs text-muted-foreground'>
                      (one-time + monthly + taxes - discounts)
                    </div>
                  </div>
                  <span className='font-bold text-xl'>
                    ${firstMonthTotal.toFixed(2)}
                  </span>
                </div>
              )}

              {/* Ongoing monthly (if has one-time charges) */}
              {totalOneTime > 0 && monthlyTotalWithTax > 0 && (
                <div className='flex justify-between items-center p-2 text-sm'>
                  <span className='text-muted-foreground'>Then monthly:</span>
                  <span className='font-medium'>
                    ${monthlyTotalWithTax.toFixed(2)}/month
                  </span>
                </div>
              )}
            </div>
          )
        })()}

        {/* Summary Stats */}
        <div className='grid grid-cols-2 gap-4 text-center'>
          <div className='p-3 border border-blue-200 rounded-lg'>
            <div className='text-lg font-bold text-blue-600'>
              {editableCharges.length}
            </div>
            <div className='text-xs text-blue-600'>Charges</div>
          </div>
          <div className='p-3 border border-green-200 rounded-lg'>
            <div className='text-lg font-bold text-green-600'>
              {addons.length}
            </div>
            <div className='text-xs text-green-600'>Add-ons</div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
